import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkGrid from "@/components/feature/WorkGrid";
import { works } from "@/mocks/works";
import { hotKeywords } from "@/mocks/banners";
import { usePageMeta } from "@/hooks/usePageMeta";
import { filterByKeyword, typeLabel } from "@/utils/work";
import type { WorkType } from "@/types";

const filters: { key: WorkType | "all"; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "variety", label: "综艺" },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [activeType, setActiveType] = useState<WorkType | "all">("all");

  useEffect(() => {
    setInput(query);
    setActiveType("all");
  }, [query]);

  usePageMeta({
    title: query
      ? `${query} 搜索结果 - 国产自拍`
      : "搜索影视作品 - 国产自拍",
    description: `在国产自拍搜索国产自制影视作品，${query ? `当前关键词：${query}。` : ""}支持按电影、电视剧、综艺筛选。`,
    keywords: `${query ? `${query},` : ""}国产自拍,影视搜索,国产影视`,
  });

  const results = useMemo(() => {
    const matched = filterByKeyword(works, query);
    return activeType === "all"
      ? matched
      : matched.filter((item) => item.type === activeType);
  }, [query, activeType]);

  const submit = (value: string) => {
    const next = value.trim();
    if (next) {
      setParams({ q: next });
    } else {
      setParams({});
    }
  };

  return (
    <div className="mx-auto w-full px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb
        items={[{ label: "首页", path: "/" }, { label: "搜索结果" }]}
      />

      <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
        <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          搜索影视作品
        </h1>
        <p className="mt-1.5 text-xs text-foreground-600 md:text-sm">
          支持按作品名称、类型、导演、演员、地区等关键词进行全文检索
        </p>

        <form
          className="mt-5 flex flex-col gap-3 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            submit(input);
          }}
        >
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3 transition-colors focus-within:border-primary-400">
            <i className="ri-search-line text-lg text-foreground-500" />
            <input
              type="text"
              name="q"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入关键词，如「悬疑」「古装」「陈默」"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 md:text-base"
            />
          </div>
          <button
            type="submit"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-7 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-accent-600"
          >
            <i className="ri-search-line text-lg" />
            搜索
          </button>
        </form>

        {!query && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500 md:text-sm">热门搜索：</span>
            {hotKeywords.map((keyword) => (
              <button
                key={keyword}
                type="button"
                onClick={() => submit(keyword)}
                className="rounded-full bg-background-200 px-3.5 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-accent-100 hover:text-accent-700 md:text-sm"
              >
                {keyword}
              </button>
            ))}
          </div>
        )}
      </div>

      {query && (
        <>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground-600">
              「<span className="font-bold text-accent-600">{query}</span>」共找到{" "}
              <span className="font-bold text-foreground-900">{results.length}</span>{" "}
              个结果
            </p>
            <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-300 bg-background-100 p-1">
              {filters.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setActiveType(item.key)}
                  className={`rounded-full px-3.5 py-1.5 text-xs transition-colors md:text-sm ${
                    activeType === item.key
                      ? "bg-primary-500 font-medium text-background-50"
                      : "text-foreground-700 hover:text-accent-600"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <WorkGrid
              works={results}
              emptyText="没有找到匹配的影视作品，换个关键词试试吧"
              emptyAction={
                <div className="flex flex-col items-center gap-3">
                  <p className="text-xs text-foreground-500">你还可以浏览以下分类</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {(["movie", "tv", "variety"] as WorkType[]).map((type) => (
                      <Link
                        key={type}
                        to={`/category/${type}`}
                        className="rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
                      >
                        {typeLabel(type)}
                      </Link>
                    ))}
                  </div>
                </div>
              }
            />
          </div>
        </>
      )}

      {!query && (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-search-eye-line text-3xl" />
          </span>
          <p className="mt-4 text-sm text-foreground-600 md:text-base">
            输入关键词，发现你想看的国产自制影视作品
          </p>
        </div>
      )}
    </div>
  );
}